<template>
    <div class="recipient-address-field">
        <ds-select-field
            v-model="selectedValue"
            class="recipient-select-field"
            :label="toLabel"
            :options="recipientOptions"
            :submitted="submitted"
            bind-value-only
            required
            @input="handleValueSelected"
        />

        <div v-if="showCustomAddressFields" class="custom-recipient-fields">
            <ds-input-field
                v-model="customValues.emailAddress"
                class="custom-recipient-field"
                name="email-address"
                :label="emailLabel"
                data-qa="custom-email-field"
                :pattern="customEmailPattern"
                :submitted="submitted"
                required
                @input="handleCustomValuesInput"
            >
                <template #error>
                    {{ emailMustBeValid }}
                </template>
            </ds-input-field>

            <ds-input-field
                v-model="customValues.name"
                class="custom-recipient-field"
                name="name"
                :label="nameLabel"
                trim
                data-qa="custom-name-field"
                @input="handleCustomValuesInput"
            />
        </div>
    </div>
</template>

<script>
import { DEFAULT_RECIPIENT_VALUES } from '@/components/broadcast/constants/emailBroadcast.constants';
import { isSpaApp } from '@/api/shared';
import {
    REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT,
    patterns as PATTERNS,
} from '@/utils/email.util';

const { emailOrLegacyMergeField: EMAIL_OR_LEGACY_MERGE_FIELD } = PATTERNS;

const MANUAL_OPTION_VALUE = 'MANUAL_EMAIL';

export default {
    props: {
        value: {
            type: String,
            required: true,
        },

        submitted: Boolean,

    },

    data() {
        return {
            selectedValue: null,
            customValues: {
                name: null,
                emailAddress: null,
            },
            showCustomAddressFields: false,
            toLabel: '',
            emailMustBeValid: '',
            nameLabel: '',
            emailLabel: '',
            workLabel: '',
            personalLabel: '',
            otherLabel: '',
            addAnEmailLabel: '',
            email1Label: '',
            email2Label: '',
            email3Label: '',
            isSpaApp: true,
            customEmailPattern: EMAIL_OR_LEGACY_MERGE_FIELD,
            customRecipientPattern: REGEX_CUSTOM_EMAIL_ADDRESS_WITH_MERGE_FIELD_SUPPORT,
        };
    },

    async created() {
        if (!this.value) {
            this.selectedValue = DEFAULT_RECIPIENT_VALUES.EMAIL;
            this.handleValueSelected(this.selectedValue);
        } else if (Object.values(DEFAULT_RECIPIENT_VALUES).includes(this.value)) {
            this.selectedValue = this.value;
        } else if (this.customRecipientPattern.test(this.value)) {
            const [, name,, emailAddress] = this.customRecipientPattern.exec(this.value);

            this.selectedValue = MANUAL_OPTION_VALUE;
            this.customValues.name = name ?? null;
            this.customValues.emailAddress = emailAddress;
            this.showCustomAddressFields = true;
        } else {
            this.selectedValue = DEFAULT_RECIPIENT_VALUES.EMAIL;
        }

        this.toLabel = this.$t('global.to');
        this.emailMustBeValid = this.$t('unlayerBuilder.emailRecipient.error.invalidCustomEmailAddress');
        this.nameLabel = this.$t('global.name');
        this.emailLabel = this.$t('global.email');
        this.workLabel = this.$t('unlayerBuilder.emailRecipient.label.work');
        this.personalLabel = this.$t('unlayerBuilder.emailRecipient.label.personal');
        this.otherLabel = this.$t('unlayerBuilder.emailRecipient.label.other');

        this.email1Label = this.$t('unlayerBuilder.emailRecipient.label.email');
        this.email2Label = this.$t('unlayerBuilder.emailRecipient.label.emailAddress2');
        this.email3Label = this.$t('unlayerBuilder.emailRecipient.label.emailAddress3');

        this.addAnEmailLabel = this.$t('unlayerBuilder.emailRecipient.label.addAnEmail');

        this.isSpaApp = await isSpaApp();
    },

    computed: {
        recipientOptions() {
            return [
                { label: this.isSpaApp ? this.workLabel : this.email1Label, value: DEFAULT_RECIPIENT_VALUES.EMAIL },
                { label: this.isSpaApp ? this.personalLabel : this.email2Label, value: DEFAULT_RECIPIENT_VALUES.EMAIL2 },
                { label: this.isSpaApp ? this.otherLabel : this.email3Label, value: DEFAULT_RECIPIENT_VALUES.EMAIL3 },
                { label: this.isSpaApp ? this.addAnEmailLabel : this.otherLabel, value: MANUAL_OPTION_VALUE },
            ];
        },

        formattedCustomAddress() {
            const { name, emailAddress } = this.customValues;

            return name ? `${name} <${emailAddress}>` : `<${emailAddress}>`;
        },

        isCustomEmailAddressValid() {
            return this.customRecipientPattern.test(this.formattedCustomAddress);
        },
    },

    methods: {
        handleValueSelected(value) {
            if (value === MANUAL_OPTION_VALUE) {
                this.showCustomAddressFields = true;

                this.$emit('custom-value-selected', this.customValues.emailAddress || '');

                return;
            }

            this.showCustomAddressFields = false;
            this.customValues.name = null;
            this.customValues.emailAddress = null;
            this.$emit('input', value);
        },

        handleCustomValuesInput() {
            if (this.isCustomEmailAddressValid) {
                this.$emit('input', this.formattedCustomAddress);
            }

            this.$emit('custom-value-selected', this.formattedCustomAddress || '');
        },
    },
};
</script>

<style lang="scss" scoped>
.custom-recipient-fields {
    display: flex;
    flex-direction: row;
}

.custom-recipient-field {
    @include margin-end($spacing-100);

    &:last-child {
        @include margin-end(0);
    }
}
</style>
